import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/services/logger';

interface Assessment {
  id: string;
  title: string;
  description: string;
  type: 'pre' | 'post' | 'interim';
  status: 'draft' | 'active' | 'completed';
  dueDate: Date;
  createdAt: Date;
  updatedAt: Date;
}

interface ParticipantAssessment {
  participantId: string;
  participantName: string;
  assessmentId: string;
  status: 'not_started' | 'in_progress' | 'completed';
  score?: number;
  completedAt?: Date;
}

interface CohortAssessmentsProps {
  isAdmin?: boolean;
}

const CohortAssessments: React.FC<CohortAssessmentsProps> = ({ isAdmin = false }) => {
  const { cohortId } = useParams<{ cohortId: string }>();
  const { currentUser } = useAuth();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [participantAssessments, setParticipantAssessments] = useState<ParticipantAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState<string>('');

  useEffect(() => {
    loadAssessments();
  }, [cohortId]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      // TODO: Implement assessment loading from Firestore
      // For now, simulate loading with sample data
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const sampleAssessments: Assessment[] = [
        {
          id: 'pre_assessment_1',
          title: 'Pre-Course Assessment',
          description: 'Initial assessment to gauge baseline knowledge',
          type: 'pre',
          status: 'active',
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          createdAt: new Date(),
          updatedAt: new Date()
        },
        {
          id: 'post_assessment_1',
          title: 'Post-Course Assessment',
          description: 'Final assessment to measure learning outcomes',
          type: 'post',
          status: 'draft',
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          createdAt: new Date(),
          updatedAt: new Date()
        }
      ];

      const sampleParticipantAssessments: ParticipantAssessment[] = [
        {
          participantId: 'participant1',
          participantName: 'John Doe',
          assessmentId: 'pre_assessment_1',
          status: 'completed',
          score: 85,
          completedAt: new Date()
        },
        {
          participantId: 'participant2',
          participantName: 'Jane Smith',
          assessmentId: 'pre_assessment_1',
          status: 'in_progress'
        }
      ];

      setAssessments(sampleAssessments);
      setParticipantAssessments(sampleParticipantAssessments);
    } catch (error) {
      logger.error('Failed to load cohort assessments', error, 'COHORT_ASSESSMENTS');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateAssessment = async (assessmentId: string) => {
    try {
      // TODO: Implement assessment activation
      setAssessments(prev => 
        prev.map(assessment => 
          assessment.id === assessmentId 
            ? { ...assessment, status: 'active' as const }
            : assessment
        )
      );
      
      logger.info('Assessment activated', { cohortId, assessmentId }, 'COHORT_ASSESSMENTS');
    } catch (error) {
      logger.error('Failed to activate assessment', error, 'COHORT_ASSESSMENTS');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'draft':
        return 'text-gray-600 bg-gray-100';
      case 'in_progress':
        return 'text-yellow-600 bg-yellow-100';
      case 'not_started':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getParticipantProgress = (assessmentId: string) => {
    const participantResults = participantAssessments.filter(
      pa => pa.assessmentId === assessmentId
    );
    
    const total = participantResults.length;
    const completed = participantResults.filter(pa => pa.status === 'completed').length;
    const inProgress = participantResults.filter(pa => pa.status === 'in_progress').length;
    
    return { total, completed, inProgress, notStarted: total - completed - inProgress };
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          {isAdmin ? 'Manage Assessments' : 'View Assessments'}
        </h1>
        
        {isAdmin && (
          <Button className="bg-blue-600 text-white">
            Create New Assessment
          </Button>
        )}
      </div>

      <div className="grid gap-6">
        {assessments.map((assessment) => {
          const progress = getParticipantProgress(assessment.id);
          
          return (
            <Card key={assessment.id} className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{assessment.title}</h3>
                  <p className="text-gray-600 mt-1">{assessment.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(assessment.status)}`}>
                    {assessment.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(assessment.type)}`}>
                    {assessment.type.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p className="font-medium">{assessment.dueDate.toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium">{assessment.createdAt.toLocaleDateString()}</p>
                </div>
              </div>

              {progress.total > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Participant Progress</p>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="text-center p-2 bg-green-50 rounded">
                      <div className="font-bold text-green-600">{progress.completed}</div>
                      <div className="text-gray-600">Completed</div>
                    </div>
                    <div className="text-center p-2 bg-yellow-50 rounded">
                      <div className="font-bold text-yellow-600">{progress.inProgress}</div>
                      <div className="text-gray-600">In Progress</div>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded">
                      <div className="font-bold text-red-600">{progress.notStarted}</div>
                      <div className="text-gray-600">Not Started</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <div className="font-bold text-gray-600">{progress.total}</div>
                      <div className="text-gray-600">Total</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex space-x-2">
                <Button variant="secondary" className="text-sm px-3 py-1">
                  View Details
                </Button>
                
                {isAdmin && assessment.status === 'draft' && (
                  <Button 
                    variant="success"
                    className="text-sm px-3 py-1"
                    onClick={() => handleActivateAssessment(assessment.id)}
                  >
                    Activate
                  </Button>
                )}
                
                {isAdmin && (
                  <Button variant="secondary" className="text-sm px-3 py-1">
                    Edit
                  </Button>
                )}
              </div>
            </Card>
          );
        })}

        {assessments.length === 0 && (
          <Card className="p-8 text-center">
            <p className="text-gray-500">No assessments available for this cohort.</p>
            {isAdmin && (
              <Button className="mt-4 bg-blue-600 text-white">
                Create First Assessment
              </Button>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default CohortAssessments;